import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '../../api/apiClient';
import {
  approveIntakeRequest,
  denyIntakeRequest,
  getIntakeRequest,
  getIntakeRequests,
} from '../../api/intakeRequestsApi';
import type {
  IntakeDecisionResponse,
  IntakeRequest,
  IntakeRequestFilter,
  IntakeRequestListItem,
  IntakeRequestStatus,
} from '../../types/intakeRequest';
import './Dashboard.css';

const STATUS_LABELS: Record<IntakeRequestStatus, string> = {
  RECEIVED: 'Received',
  APPROVED: 'Approved',
  DENIED: 'Denied',
};

const VALUE_LABELS: Record<string, string> = {
  WEB_APPLICATION: 'Web application',
  MOBILE_APPLICATION: 'Mobile application',
  INTERNAL_SYSTEM: 'Internal business system',
  INTEGRATION: 'System integration',
  EXISTING_PRODUCT: 'Existing product improvement',
  OTHER: 'Other',
  UNDER_3_MONTHS: 'Under 3 months',
  THREE_TO_SIX_MONTHS: '3 to 6 months',
  SIX_TO_TWELVE_MONTHS: '6 to 12 months',
  OVER_TWELVE_MONTHS: 'More than 12 months',
  FLEXIBLE: 'Flexible',
  UNDER_2500: 'Under $2,500',
  BETWEEN_2500_AND_5000: '$2,500 to $5,000',
  BETWEEN_5000_AND_10000: '$5,000 to $10,000',
  BETWEEN_10000_AND_20000: '$10,000 to $20,000',
  OVER_20000: 'More than $20,000',
  UNDETERMINED: 'Not sure yet',
};

function formatEnum(value: string | null | undefined): string {
  if (value && VALUE_LABELS[value]) {
    return VALUE_LABELS[value];
  }

  return String(value || 'Not provided')
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/^./, (character) => character.toUpperCase());
}

function formatDate(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString() : 'Not provided';
}

function IntakeRequests() {
  const [statusFilter, setStatusFilter] = useState<IntakeRequestFilter>('RECEIVED');
  const [requests, setRequests] = useState<IntakeRequestListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<IntakeRequest | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState<'' | 'approve' | 'deny'>('');
  const [communicationConfirmed, setCommunicationConfirmed] = useState(false);
  const [decisionNote, setDecisionNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setListLoading(true);
    setError('');

    getIntakeRequests(statusFilter)
      .then((response) => {
        if (!active) {
          return;
        }

        const items = response.items || [];
        setRequests(items);
        setSelectedId((current) => items.some((item) => item.id === current) ? current : items[0]?.id || null);
        if (items.length === 0) {
          setSelectedRequest(null);
        }
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(getErrorMessage(requestError, 'Project requests could not be loaded.'));
        }
      })
      .finally(() => {
        if (active) {
          setListLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [statusFilter]);

  useEffect(() => {
    if (!selectedId) {
      return undefined;
    }

    let active = true;
    setDetailLoading(true);
    setError('');

    getIntakeRequest(selectedId)
      .then((response) => {
        if (active) {
          setSelectedRequest(response);
          setCommunicationConfirmed(false);
          setDecisionNote('');
        }
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(getErrorMessage(requestError, 'The project request could not be loaded.'));
        }
      })
      .finally(() => {
        if (active) {
          setDetailLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedId]);

  const applyDecisionResult = (response: IntakeDecisionResponse) => {
    if (!selectedRequest) {
      return;
    }

    const updatedRequest = { ...selectedRequest, ...response.request };
    setSelectedRequest(updatedRequest);
    setCommunicationConfirmed(false);
    setDecisionNote('');
    setRequests((current) => {
      const updated = current.map((item) => item.id === updatedRequest.id ? { ...item, ...updatedRequest } : item);
      return statusFilter === 'ALL' ? updated : updated.filter((item) => item.status === statusFilter);
    });
  };

  const handleApprove = async () => {
    if (!selectedRequest || !communicationConfirmed) {
      return;
    }

    setProcessingAction('approve');
    setError('');

    try {
      const response = await approveIntakeRequest(selectedRequest.id, {
        communicationConfirmed: true,
        decisionNote: decisionNote.trim() || null,
      });
      applyDecisionResult(response);
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'The project request could not be approved.'));
    } finally {
      setProcessingAction('');
    }
  };

  const handleDeny = async () => {
    if (!selectedRequest || !communicationConfirmed) {
      return;
    }

    if (!decisionNote.trim()) {
      setError('A decision note is required to deny a request.');
      return;
    }

    setProcessingAction('deny');
    setError('');

    try {
      const response = await denyIntakeRequest(selectedRequest.id, {
        communicationConfirmed: true,
        decisionNote: decisionNote.trim(),
      });
      applyDecisionResult(response);
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'The project request could not be denied.'));
    } finally {
      setProcessingAction('');
    }
  };

  return (
    <section className="dashboard-page">
      <div className="dashboard-page-header">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Project requests</h1>
        </div>
        <label className="request-filter" htmlFor="request-status-filter">
          <span>Status</span>
          <select
            id="request-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as IntakeRequestFilter)}
          >
            <option value="RECEIVED">Received</option>
            <option value="APPROVED">Approved</option>
            <option value="DENIED">Denied</option>
            <option value="ALL">All</option>
          </select>
        </label>
      </div>

      {error && <p className="dashboard-error" role="alert">{error}</p>}

      <div className="request-review-grid">
        <section className="dashboard-panel request-list" aria-label="Project requests">
          {listLoading && <p className="inline-empty-state" role="status">Loading requests...</p>}
          {!listLoading && requests.map((request) => (
            <button
              key={request.id}
              type="button"
              className={`request-list-item ${selectedId === request.id ? 'selected' : ''}`}
              onClick={() => setSelectedId(request.id)}
              aria-pressed={selectedId === request.id}
            >
              <span>
                <strong>{request.projectName}</strong>
                <small>{request.contactName} | {formatDate(request.submittedAt)}</small>
              </span>
              <span className={`request-status request-status-${request.status.toLowerCase()}`}>
                {STATUS_LABELS[request.status] || formatEnum(request.status)}
              </span>
            </button>
          ))}
          {!listLoading && requests.length === 0 && <p className="inline-empty-state">No requests match this status.</p>}
        </section>

        <section className="dashboard-panel request-detail" aria-live="polite">
          {detailLoading && <p className="inline-empty-state" role="status">Loading request...</p>}
          {!detailLoading && !selectedRequest && <p className="inline-empty-state">Select a request to review.</p>}
          {!detailLoading && selectedRequest && (
            <>
              <div className="request-detail-header">
                <div>
                  <p className="eyebrow">{selectedRequest.reference}</p>
                  <h2>{selectedRequest.projectName}</h2>
                </div>
                <span className={`request-status request-status-${selectedRequest.status.toLowerCase()}`}>
                  {STATUS_LABELS[selectedRequest.status] || formatEnum(selectedRequest.status)}
                </span>
              </div>

              <dl className="request-detail-list">
                <div><dt>Contact</dt><dd>{selectedRequest.contactName}<br /><a href={`mailto:${selectedRequest.email}`}>{selectedRequest.email}</a>{selectedRequest.phone && <><br />{selectedRequest.phone}</>}</dd></div>
                <div><dt>Organization</dt><dd>{selectedRequest.organization || 'Not provided'}</dd></div>
                <div><dt>Project type</dt><dd>{formatEnum(selectedRequest.projectType)}</dd></div>
                <div><dt>Timeline</dt><dd>{formatEnum(selectedRequest.timeline)}</dd></div>
                <div><dt>Budget</dt><dd>{formatEnum(selectedRequest.budgetRange)}</dd></div>
                <div className="request-detail-full"><dt>Business intention</dt><dd>{selectedRequest.intention}</dd></div>
                <div className="request-detail-full"><dt>Expected scope</dt><dd>{selectedRequest.scope}</dd></div>
                <div className="request-detail-full"><dt>Intended users</dt><dd>{selectedRequest.targetUsers || 'Not provided'}</dd></div>
                <div className="request-detail-full"><dt>Additional context</dt><dd>{selectedRequest.additionalContext || 'Not provided'}</dd></div>
              </dl>

              {selectedRequest.status === 'RECEIVED' && (
                <div className="request-decision">
                  <label htmlFor="decision-note">Decision note</label>
                  <textarea
                    id="decision-note"
                    rows={4}
                    value={decisionNote}
                    onChange={(event) => setDecisionNote(event.target.value)}
                  />
                  <label className="request-confirmation">
                    <input
                      type="checkbox"
                      checked={communicationConfirmed}
                      onChange={(event) => setCommunicationConfirmed(event.target.checked)}
                    />
                    <span>Client communication completed</span>
                  </label>
                  <div className="request-actions">
                    <button type="button" className="text-button request-deny" onClick={handleDeny} disabled={Boolean(processingAction) || !communicationConfirmed}>
                      {processingAction === 'deny' ? 'Denying...' : 'Deny'}
                    </button>
                    <button type="button" className="dashboard-action" onClick={handleApprove} disabled={Boolean(processingAction) || !communicationConfirmed}>
                      {processingAction === 'approve' ? 'Approving...' : 'Approve and invite'}
                    </button>
                  </div>
                </div>
              )}

              {selectedRequest.projectId && (
                <Link className="dashboard-action request-project-link" to={`/dashboard/projects/${selectedRequest.projectId}`}>
                  View project
                </Link>
              )}
            </>
          )}
        </section>
      </div>
    </section>
  );
}

export default IntakeRequests;
